import { zodResolver } from "@hookform/resolvers/zod";
import {  t  } from "@/client/libs/dummy-i18n";
import {  useLingui  } from "@/client/libs/dummy-i18n";
import { ArrowRightIcon, CircleNotchIcon } from "@phosphor-icons/react";
import { loginSchema } from "@stackmorph/dto";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@stackmorph/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect,useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import type { z } from "zod";

import { queryClient } from "@/client/libs/query-client";
import { useLogin } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";

const formSchema = loginSchema;

type FormValues = z.infer<typeof formSchema>;

const LoginMascot = ({ 
  isTypingPassword,
  isTypingEmail,
  hasError,
  emailLength,
}: { 
  isTypingPassword: boolean;
  isTypingEmail: boolean;
  hasError: boolean;
  emailLength: number;
}) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Track mouse movement for eye following
  useEffect(() => {
    let animationFrameId: number;
    
    const handleMouseMove = (e: MouseEvent) => {
      // If typing, don't track mouse
      if (isTypingPassword || isTypingEmail) return;

      // Use requestAnimationFrame to throttle updates
      cancelAnimationFrame(animationFrameId);
      
      animationFrameId = requestAnimationFrame(() => {
        // Calculate position relative to center of screen
        // Map the full screen width/height to the eye movement range (-10 to 10)
        const x = ((e.clientX / window.innerWidth) - 0.5) * 24; 
        const y = ((e.clientY / window.innerHeight) - 0.5) * 24;
        
        // Clamp values to keep pupils in eyes (max range is +/- 10)
        const clampedX = Math.max(-10, Math.min(10, x));
        const clampedY = Math.max(-10, Math.min(10, y));
        
        setMousePos({ x: clampedX, y: clampedY });
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isTypingPassword, isTypingEmail]);

  // Eye position logic:
  // 1. Typing password: look up-center (0, -8)
  // 2. Typing email: look down-left (-6, 10)
  // 3. Default: follow mouse
  
  const getEyePosition = () => {
    if (isTypingPassword) return { x: 0, y: -8 };
    if (isTypingEmail) return { x: -6, y: 10 };
    return mousePos;
  };

  const eyePos = getEyePosition();

  return (
    <div className="relative mx-auto mb-8 flex h-40 w-40 items-center justify-center">
      <motion.div
        animate={{
          rotate: hasError ? [0, -6, 6, -6, 0] : 0,
        }}
        transition={{ duration: 0.5 }}
        className="relative"
      >
        {/* Simple Cat Head */}
        <div className="relative h-28 w-28">
          
          {/* Ears */}
          <div className="absolute -left-1 -top-4 h-12 w-10 origin-bottom-right rotate-[-30deg] rounded-t-full bg-gray-900">
            {/* Pink inner ear */}
            <div className="absolute inset-x-2 top-2 h-6 w-6 rounded-t-full bg-pink-300/60" />
          </div>
          <div className="absolute -right-1 -top-4 h-12 w-10 origin-bottom-left rotate-[30deg] rounded-t-full bg-gray-900">
            {/* Pink inner ear */}
            <div className="absolute inset-x-2 top-2 h-6 w-6 rounded-t-full bg-pink-300/60" />
          </div>

          {/* Head Circle */}
          <div className="relative h-28 w-28 rounded-full bg-gray-900 shadow-2xl">
            
            {/* Eyes positioned properly - looking DOWN at form */}
            <div className="absolute left-1/2 top-9 flex -translate-x-1/2 gap-4">
              
              {/* Left Eye */}
              <div className="relative h-11 w-11 overflow-hidden rounded-full bg-yellow-400 shadow-md">
                {/* Pupil */}
                <motion.div
                  animate={{
                    x: eyePos.x,
                    y: eyePos.y,
                  }}
                  transition={{ type: "spring", stiffness: 150, damping: 15 }}
                  className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900"
                >
                  <div className="absolute right-1 top-0.5 h-2 w-2 rounded-full bg-white" />
                </motion.div>
              </div>

              {/* Right Eye */}
              <div className="relative h-11 w-11 overflow-hidden rounded-full bg-yellow-400 shadow-md">
                {/* Pupil */}
                <motion.div
                  animate={{
                    x: eyePos.x,
                    y: eyePos.y,
                  }}
                  transition={{ type: "spring", stiffness: 150, damping: 15 }}
                  className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900"
                >
                  <div className="absolute right-1 top-0.5 h-2 w-2 rounded-full bg-white" />
                </motion.div>
              </div>
            </div>

            {/* Pink Nose */}
            <div className="absolute bottom-8 left-1/2 h-2 w-2.5 -translate-x-1/2 rounded-full bg-pink-400" />

            {/* Sweat when error */}
            {hasError && (
              <>
                <motion.div
                  initial={{ y: 0, opacity: 0 }}
                  animate={{
                    y: [0, 12, 24],
                    opacity: [0, 1, 0],
                  }}
                  transition={{ duration: 0.8 }}
                  className="absolute left-2 top-4 h-3 w-2 rounded-b-full bg-cyan-400"
                />
                <motion.div
                  initial={{ y: 0, opacity: 0 }}
                  animate={{
                    y: [0, 12, 24],
                    opacity: [0, 1, 0],
                  }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="absolute right-2 top-4 h-3 w-2 rounded-b-full bg-cyan-400"
                />
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const CinematicSuccessAnimation = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-xl"
    >
      <div className="relative flex flex-col items-center justify-center">
        {/* Pulsating Glow */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ 
            scale: [0.8, 1.2, 1],
            opacity: [0, 0.5, 0.3]
          }}
          transition={{ duration: 2, ease: "easeInOut", repeat: Infinity }}
          className="absolute inset-0 -z-10 rounded-full bg-primary/30 blur-3xl"
        />

        {/* Logo / Icon */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "backOut" }}
          className="mb-8 flex size-24 items-center justify-center rounded-3xl bg-gradient-to-br from-primary to-primary/50 shadow-2xl shadow-primary/30"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-white"
          >
            <path
              d="M12 2L2 7L12 12L22 7L12 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 17L12 22L22 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 12L12 17L22 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>

        {/* Text Reveal */}
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-3xl font-bold tracking-tight"
          >
            {t`Welcome Back`}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-2 text-muted-foreground"
          >
            {t`Initializing your workspace...`}
          </motion.p>
        </div>

        {/* Loading Spinner */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <CircleNotchIcon className="animate-spin text-primary" size={24} />
        </motion.div>
      </div>
    </motion.div>
  );
};

export const LoginPage = () => {
  const { i18n } = useLingui();
  const { login, loading } = useLogin();
  const setUser = useAuthStore((state) => state.setUser);
  
  const [isTypingPassword, setIsTypingPassword] = useState(false);
  const [isTypingEmail, setIsTypingEmail] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [emailLength, setEmailLength] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { identifier: "", password: "" },
  });

  const [pendingUser, setPendingUser] = useState<any>(null);

  const onSubmit = async (data: FormValues) => {
    try {
      setHasError(false);
      const result = await login(data);
      
      // If login successful (and not 2FA), trigger animation
      if (result && result.user) {
        setIsSuccess(true);
        setPendingUser(result.user);
      }
    } catch {
      setHasError(true);
      setTimeout(() => {
        setHasError(false);
      }, 2000);
    }
  };

  // Handle success redirect with cleanup
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (pendingUser) {
      timeoutId = setTimeout(() => {
        setUser(pendingUser);
        queryClient.setQueryData(["user"], pendingUser);
      }, 2500);
    }

    return () => { clearTimeout(timeoutId); };
  }, [pendingUser, setUser]);

  // Handle success redirect with cleanup
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSuccess) {
      timeoutId = setTimeout(() => {
        // Get the user from the query cache or store if available, 
        // but since we don't have the user object here easily without prop drilling or context,
        // we rely on the fact that useLogin likely updated the cache or we re-fetch.
        // Actually, looking at the previous code, result.user was passed to setUser.
        // To keep it clean, we should probably fetch the user or rely on the auth store update if useLogin handles it.
        // However, the previous code manually called setUser. 
        // Let's refactor slightly to ensure we have the user data.
        // Since we can't easily pass data from onSubmit to useEffect without state, 
        // we'll assume the queryClient has the user or we fetch it.
        // BETTER APPROACH: Just use the timeout in onSubmit but clear it in useEffect cleanup.
      }, 2500);
    }

    return () => { clearTimeout(timeoutId); };
  }, [isSuccess, setUser]);
  
  // WAIT - The above approach is tricky because we need the `result.user` from `onSubmit`.
  // Let's stick to the simpler fix: Use a ref to track mounted state or just a simple useEffect cleanup.
  // Actually, the cleanest way in React for this specific "delay then action" pattern where data is involved
  // is to store the pending user in state.

  /* 
     Re-thinking the replacement to be robust and simple:
     1. Add state `pendingUser`
     2. In onSubmit, set `pendingUser` and `isSuccess`
     3. In useEffect, if `pendingUser` is set, start timeout to call `setUser(pendingUser)`
  */

  return (
    <div className="relative">
      <Helmet prioritizeSeoTags>
        <html lang={i18n.locale} />
        <title>
          {t`Sign in to your account`} - {'StackMorph'}
        </title>
      </Helmet>

      <AnimatePresence>
        {isSuccess && <CinematicSuccessAnimation />}
      </AnimatePresence>

      <motion.div
        animate={isSuccess ? { scale: 0.95, filter: "blur(4px)", opacity: 0.5 } : { scale: 1, filter: "blur(0px)", opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <LoginMascot isTypingPassword={isTypingPassword} isTypingEmail={isTypingEmail} hasError={hasError} emailLength={emailLength} />

        <div className="space-y-2 text-center">
          <h2 className="text-3xl font-bold">{t`Welcome back`}</h2>
          <p className="text-sm text-muted-foreground">
            {t`Don't have an account?`}{" "}
            <Link to="/auth/register" className="font-medium text-primary hover:underline">
              {t`Sign up`}
            </Link>
          </p>
        </div>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              name="identifier"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Email`}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="you@example.com"
                      onFocus={() => { setIsTypingEmail(true); }}
                      onBlur={() => { setIsTypingEmail(false); }}
                      onChange={(e) => {
                        setEmailLength(e.target.value.length);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Password`}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      onFocus={() => { setIsTypingPassword(true); }}
                      onBlur={() => { setIsTypingPassword(false); }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between text-sm">
              <Link
                to="/auth/forgot-password"
                className="font-medium text-primary hover:underline"
              >
                {t`Forgot password?`}
              </Link>
            </div>

            <Button type="submit" disabled={loading || isSuccess} className="w-full">
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full"
                />
              ) : (
                <>
                  {t`Sign in`}
                  <ArrowRightIcon className="ml-2" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </motion.div>
    </div>
  );
};
